import { useState } from "react";
import { Building2, Plus, Settings, Check, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { ContactGroup } from "@/types/lead";

interface GroupDropdownMenuProps {
  groups: ContactGroup[];
  selectedGroupId: string | null;
  onSelectGroup: (id: string) => void;
  onOpenManager: () => void;
  loading?: boolean;
  groupLeadCounts?: Record<string, number>;
}

export function GroupDropdownMenu({
  groups,
  selectedGroupId,
  onSelectGroup,
  onOpenManager,
  loading = false,
  groupLeadCounts = {},
}: GroupDropdownMenuProps) {
  const [open, setOpen] = useState(false);

  const selectedGroup = groups.find((g) => g.id === selectedGroupId);

  const handleSelectGroup = (id: string) => {
    onSelectGroup(id);
    setOpen(false);
  };

  const handleOpenManager = () => {
    setOpen(false);
    onOpenManager();
  };

  const getGroupIcon = (type?: string) => {
    switch (type) {
      case "empresa":
        return Building2;
      case "familia":
      case "parceiro":
      case "fornecedor":
      default:
        return Users;
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="min-w-0 max-w-full shrink"
          disabled={loading}
        >
          <Building2 className="w-4 h-4 shrink-0 sm:mr-2" />
          <span className="hidden sm:inline min-w-0 truncate">
            {loading ? "Carregando..." : selectedGroup?.name || "Selecionar Grupo"}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 bg-popover border shadow-lg z-50">
        <DropdownMenuLabel>Selecionar Grupo</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {groups.length === 0 ? (
          <div className="px-2 py-4 text-center text-sm text-muted-foreground">
            Nenhum grupo cadastrado.
            <br />
            Clique em "Gerenciar Grupos" para criar.
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            {groups.map((group) => {
              const Icon = getGroupIcon(group.type);
              const leadCount = groupLeadCounts[group.id] || 0;

              return (
                <DropdownMenuItem
                  key={group.id}
                  onClick={() => handleSelectGroup(group.id)}
                  className="flex items-center gap-3 cursor-pointer py-2"
                >
                  <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="block truncate font-medium">{group.name}</span>
                    {group.type && (
                      <span className="block text-xs text-muted-foreground capitalize">
                        {group.type}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {leadCount} {leadCount === 1 ? "lead" : "leads"}
                  </span>
                  {group.id === selectedGroupId && (
                    <Check className="w-4 h-4 text-primary shrink-0" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </div>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleOpenManager} className="cursor-pointer">
          <Settings className="w-4 h-4 mr-2" />
          Gerenciar Grupos
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
